/**
 * Notification preference helpers.
 *
 * Two rules keep this file free of catalog knowledge:
 *  1. The UI thinks in "enabled" ids; the API stores "excluded" ids. Convert only
 *     at the network boundary with mutedToEnabled / enabledToMuted.
 *  2. Presets and grouping are derived from the catalog response itself (the
 *     `exclude` flag and the `category` field), never from a list in code.
 */

export interface DescriptorLike {
  id: number;
  category: string;
  descriptor: string;
  exclude?: string | null;
}

export interface DescriptorsByCategoryLike<T extends DescriptorLike = DescriptorLike> {
  [category: string]: T[];
}

/**
 * Presets are catalog-derived: "recommended" is whatever ops marked exclude=No.
 * There is deliberately no "everything" preset - a single tap that opts into every
 * alert type is noise the user cannot judge before choosing it.
 */
export type PresetId = "recommended" | "custom";

export type ToggleState = "enabled" | "muted" | "partial";

/** Which rows to show; used to keep large categories readable. */
export type VisibilityFilter = "all" | "enabled" | "muted";

const isYes = (flag: string | null | undefined): boolean =>
  (flag ?? "").trim().toLowerCase() === "yes";

export const flattenDescriptors = <T extends DescriptorLike>(
  byCategory: DescriptorsByCategoryLike<T>
): T[] => Object.values(byCategory).flat();

export const allDescriptorIds = <T extends DescriptorLike>(
  byCategory: DescriptorsByCategoryLike<T>
): number[] => flattenDescriptors(byCategory).map((descriptor) => descriptor.id);

/** Catalog rows sorted by name, so the order follows the data and not a fixed list. */
export const sortedCategoryNames = <T extends DescriptorLike>(
  byCategory: DescriptorsByCategoryLike<T>
): string[] => Object.keys(byCategory).sort((a, b) => a.localeCompare(b));

export const mutedToEnabled = (allIds: number[], mutedIds: number[]): number[] => {
  const muted = new Set(mutedIds);
  return allIds.filter((id) => !muted.has(id));
};

export const enabledToMuted = (allIds: number[], enabledIds: number[]): number[] => {
  const enabled = new Set(enabledIds);
  return allIds.filter((id) => !enabled.has(id));
};

/**
 * Mutes the API still holds for ids that are no longer in the catalog. The save
 * endpoint replaces the whole mute list, so these must be sent back unchanged or
 * a successful save would silently unmute retired alert types.
 */
export const orphanMutedIds = (allIds: number[], mutedIds: number[]): number[] => {
  const catalog = new Set(allIds);
  return mutedIds.filter((id) => !catalog.has(id));
};

/** Catalog mutes from the UI, plus any orphan mutes still stored on the profile. */
export const mutedIdsForSave = (
  allIds: number[],
  enabledIds: number[],
  preservedOrphanMutedIds: number[]
): number[] => {
  const catalogMuted = enabledToMuted(allIds, enabledIds);
  if (preservedOrphanMutedIds.length === 0) {
    return catalogMuted;
  }
  return Array.from(new Set([...catalogMuted, ...preservedOrphanMutedIds]));
};

/** Ops-owned default: every row the catalog does not mark exclude=Yes. */
export const recommendedEnabledIds = <T extends DescriptorLike>(
  byCategory: DescriptorsByCategoryLike<T>
): number[] =>
  flattenDescriptors(byCategory)
    .filter((descriptor) => !isYes(descriptor.exclude))
    .map((descriptor) => descriptor.id);

export const isDefaultMuted = (descriptor: DescriptorLike): boolean => isYes(descriptor.exclude);

export const sameIdSet = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
};

export const enabledIdsForPreset = <T extends DescriptorLike>(
  preset: Exclude<PresetId, "custom">,
  byCategory: DescriptorsByCategoryLike<T>
): number[] => {
  switch (preset) {
    case "recommended":
      return recommendedEnabledIds(byCategory);
    default: {
      const exhaustive: never = preset;
      return exhaustive;
    }
  }
};

export const detectPreset = <T extends DescriptorLike>(
  enabledIds: number[],
  byCategory: DescriptorsByCategoryLike<T>
): PresetId => {
  if (sameIdSet(enabledIds, recommendedEnabledIds(byCategory))) {
    return "recommended";
  }
  return "custom";
};

export const countEnabled = (ids: number[], enabledIds: number[]): number => {
  const enabled = new Set(enabledIds);
  return ids.filter((id) => enabled.has(id)).length;
};

export const toggleState = (ids: number[], enabledIds: number[]): ToggleState => {
  if (ids.length === 0) {
    return "muted";
  }
  const enabledCount = countEnabled(ids, enabledIds);
  if (enabledCount === 0) {
    return "muted";
  }
  if (enabledCount === ids.length) {
    return "enabled";
  }
  return "partial";
};

export const toggleId = (enabledIds: number[], id: number): number[] =>
  enabledIds.includes(id) ? enabledIds.filter((current) => current !== id) : [...enabledIds, id];

export const setGroupEnabled = (
  enabledIds: number[],
  ids: number[],
  turnOn: boolean
): number[] => {
  const next = new Set(enabledIds);
  for (const id of ids) {
    if (turnOn) {
      next.add(id);
    } else {
      next.delete(id);
    }
  }
  return Array.from(next);
};

/** Number of rows whose enabled/muted value differs from what is saved. */
export const changedCount = (
  allIds: number[],
  enabledIds: number[],
  savedEnabledIds: number[]
): number => {
  const enabled = new Set(enabledIds);
  const saved = new Set(savedEnabledIds);
  return allIds.filter((id) => enabled.has(id) !== saved.has(id)).length;
};

export const matchesQuery = (descriptor: DescriptorLike, query: string): boolean => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  return (
    descriptor.descriptor.toLowerCase().includes(normalized) ||
    descriptor.category.toLowerCase().includes(normalized)
  );
};

export const passesVisibility = (
  descriptor: DescriptorLike,
  enabledIds: number[],
  filter: VisibilityFilter
): boolean => {
  switch (filter) {
    case "all":
      return true;
    case "enabled":
      return enabledIds.includes(descriptor.id);
    case "muted":
      return !enabledIds.includes(descriptor.id);
    default: {
      const exhaustive: never = filter;
      return exhaustive;
    }
  }
};

/** Rows to render for a category after search and visibility are applied. */
export const visibleDescriptors = <T extends DescriptorLike>(
  descriptors: T[],
  enabledIds: number[],
  query: string,
  filter: VisibilityFilter
): T[] =>
  descriptors.filter(
    (descriptor) =>
      matchesQuery(descriptor, query) && passesVisibility(descriptor, enabledIds, filter)
  );
